import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Users, Package, TrendingUp, Boxes, Clock, Receipt, AlertTriangle } from "lucide-react";

interface ProfileRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
}

interface SubscriptionRow {
  id: string;
  status: string;
  tier_tubes: number;
  monthly_price_cents: number | null;
  pending_effective_date: string | null;
}

interface OrderRow {
  id: string;
  user_id: string;
  product_name: string;
  status: string;
  payment_status: string | null;
  total_cents: number | null;
  created_at: string;
  order_mode: string | null;
  quantity: number | null;
}

const formatSEK = (cents: number) =>
  new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(cents / 100);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { day: "numeric", month: "short" });

const monthBoundsUTC = () => {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { start: start.toISOString(), end: end.toISOString() };
};

const AdminDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [subs, setSubs] = useState<SubscriptionRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [needsAction, setNeedsAction] = useState<OrderRow[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [profilesRes, subsRes, ordersRes, needsRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, email, first_name, last_name, company, status, created_at")
          .order("created_at", { ascending: false }),
        supabase
          .from("subscriptions")
          .select("id, status, tier_tubes, monthly_price_cents, pending_effective_date"),
        supabase
          .from("orders")
          .select("id, user_id, product_name, status, payment_status, total_cents, created_at, order_mode, quantity")
          .not("payment_status", "in", "(awaiting_payment,cancelled,failed,requires_action)")
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("orders")
          .select("id, user_id, product_name, status, payment_status, total_cents, created_at, order_mode, quantity")
          .in("payment_status", ["requires_action", "failed"])
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

      if (profilesRes.error) toast.error("Kunde inte ladda kunder");
      if (subsRes.error) toast.error("Kunde inte ladda prenumerationer");
      if (ordersRes.error) toast.error("Kunde inte ladda ordrar");

      setProfiles(profilesRes.data ?? []);
      setSubs(subsRes.data ?? []);
      setOrders(ordersRes.data ?? []);
      setNeedsAction((needsRes.data ?? []) as OrderRow[]);
      setLoading(false);
    };
    load();
  }, []);

  const profileMap = useMemo(() => {
    const map = new Map<string, ProfileRow>();
    profiles.forEach((p) => map.set(p.id, p));
    return map;
  }, [profiles]);

  const stats = useMemo(() => {
    const activeSubs = subs.filter((s) => s.status === "active");
    const mrr = activeSubs.reduce((acc, s) => acc + (s.monthly_price_cents ?? 0), 0);
    const subscribedTubes = activeSubs.reduce((acc, s) => acc + (s.tier_tubes ?? 0), 0);
    const pendingChanges = subs.filter((s) => s.pending_effective_date).length;

    const { start, end } = monthBoundsUTC();
    const thisMonthOrders = orders.filter(
      (o) => o.created_at >= start && o.created_at < end,
    );
    const pendingDeliveries = thisMonthOrders.filter((o) => o.status === "pending").length;
    const shippedDeliveries = thisMonthOrders.filter((o) => o.status === "shipped").length;
    const deliveredTubes = thisMonthOrders.reduce((acc, o) => acc + (o.quantity ?? 0), 0);

    const pendingApprovals = profiles.filter((p) => p.status === "pending").length;
    const unpaidInvoices = orders.filter(
      (o) => (o.payment_status === "pending" || o.payment_status === "unpaid") && o.order_mode !== "subscription",
    ).length;

    return {
      activeSubs: activeSubs.length,
      mrr,
      subscribedTubes,
      deliveredTubes,
      pendingChanges,
      pendingDeliveries,
      shippedDeliveries,
      pendingApprovals,
      unpaidInvoices,
    };
  }, [subs, orders, profiles]);

  const recentOrders = useMemo(() => orders.slice(0, 5), [orders]);
  const recentCustomers = useMemo(() => profiles.slice(0, 5), [profiles]);

  const kpis = [
    {
      label: "Aktiva prenumerationer",
      value: stats.activeSubs.toString(),
      icon: Package,
      hint: stats.pendingChanges > 0 ? `${stats.pendingChanges} med planerad ändring` : undefined,
    },
    {
      label: "Leveranser denna månad",
      value: `${stats.shippedDeliveries} / ${stats.shippedDeliveries + stats.pendingDeliveries}`,
      icon: Boxes,
      hint: stats.pendingDeliveries > 0 ? `${stats.pendingDeliveries} kvar att skicka` : "Allt skickat",
    },
    {
      label: "MRR",
      value: formatSEK(stats.mrr),
      icon: TrendingUp,
      hint: "Återkommande månadsintäkt",
    },
    {
      label: "Tuber per månad",
      value: `${stats.deliveredTubes} / ${stats.subscribedTubes}`,
      icon: Boxes,
      hint: "Levererat denna månad / abonnerad baseline",
    },
    {
      label: "Väntande godkännanden",
      value: stats.pendingApprovals.toString(),
      icon: Clock,
      hint: stats.pendingApprovals > 0 ? "Kräver åtgärd" : "Inga just nu",
    },
    {
      label: "Obetalda fakturor",
      value: stats.unpaidInvoices.toString(),
      icon: Receipt,
      hint: "Engångsordrar utan betalning",
    },
  ];

  const statusBadge = (status: string) => {
    const variants: Record<string, string> = {
      pending: "bg-amber-100 text-amber-900 hover:bg-amber-100",
      shipped: "bg-emerald-100 text-emerald-900 hover:bg-emerald-100",
      paid: "bg-emerald-100 text-emerald-900 hover:bg-emerald-100",
      cancelled: "bg-muted text-muted-foreground hover:bg-muted",
    };
    return <Badge className={variants[status] ?? "bg-muted text-muted-foreground hover:bg-muted"}>{status}</Badge>;
  };

  const profileStatusBadge = (status: ProfileRow["status"]) => {
    const variants: Record<ProfileRow["status"], string> = {
      pending: "bg-amber-100 text-amber-900 hover:bg-amber-100",
      approved: "bg-emerald-100 text-emerald-900 hover:bg-emerald-100",
      rejected: "bg-rose-100 text-rose-900 hover:bg-rose-100",
    };
    return <Badge className={variants[status]}>{status}</Badge>;
  };

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8">
        <p className="text-sm text-muted-foreground">
          Översikt över verksamheten — uppdateras live från databasen.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {kpis.map(({ label, value, icon: Icon, hint }) => (
            <Card key={label}>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {label}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-semibold text-foreground">{value}</div>
                )}
                {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {needsAction.length > 0 && (
          <Card className="border-amber-300 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                Kräver åtgärd ({needsAction.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-amber-200">
                {needsAction.slice(0, 8).map((o) => {
                  const customer = profileMap.get(o.user_id);
                  const name = customer
                    ? [customer.first_name, customer.last_name].filter(Boolean).join(" ") || customer.email
                    : "Okänd kund";
                  const label = o.payment_status === "requires_action" ? "Väntar 3DS-bekräftelse" : "Betalning misslyckad";
                  return (
                    <li key={o.id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Link
                          to={customer ? `/admin/customers/${customer.id}` : "/admin/deliveries"}
                          className="text-sm font-medium text-foreground hover:underline truncate block"
                        >
                          {name}
                        </Link>
                        <p className="text-xs text-amber-800 truncate">
                          {label} · {o.product_name} · {formatDate(o.created_at)}
                        </p>
                      </div>
                      {o.total_cents != null && (
                        <span className="text-sm font-mono tabular-nums text-foreground">
                          {formatSEK(o.total_cents)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Senaste ordrar</CardTitle>
                <Link to="/admin/deliveries" className="text-xs text-muted-foreground hover:text-foreground">
                  Visa alla →
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : recentOrders.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga ordrar än.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {recentOrders.map((o) => {
                    const customer = profileMap.get(o.user_id);
                    const name = customer
                      ? [customer.first_name, customer.last_name].filter(Boolean).join(" ") || customer.email
                      : "Okänd kund";
                    return (
                      <li key={o.id} className="py-2.5 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <Link
                            to={customer ? `/admin/customers/${customer.id}` : "/admin/customers"}
                            className="text-sm font-medium text-foreground hover:underline truncate block"
                          >
                            {name}
                          </Link>
                          <p className="text-xs text-muted-foreground truncate">
                            {o.product_name} · {formatDate(o.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {o.total_cents != null && (
                            <span className="text-sm font-mono tabular-nums text-foreground">
                              {formatSEK(o.total_cents)}
                            </span>
                          )}
                          {statusBadge(o.status)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Senaste kunder
                </CardTitle>
                <Link to="/admin/customers" className="text-xs text-muted-foreground hover:text-foreground">
                  Visa alla →
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : recentCustomers.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga kunder än.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {recentCustomers.map((c) => {
                    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;
                    return (
                      <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/admin/customers/${c.id}`}
                            className="text-sm font-medium text-foreground hover:underline truncate block"
                          >
                            {name}
                          </Link>
                          <p className="text-xs text-muted-foreground truncate">
                            {c.company || c.email} · {formatDate(c.created_at)}
                          </p>
                        </div>
                        {profileStatusBadge(c.status)}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
